import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { API_TARGET } from '../lib/auth-config';
import { loadProfile, useSession } from '../lib/session';
import { TABLES, tableFor } from '../data/seating-plan';

/**
 * Be another guest, to see the site from their table (D73).
 *
 * Moves this session's claim onto any roster row through the mock's
 * `/dev/hold`, then rereads `/me`, so the Trip card, the seating plan and the
 * flights all answer as that guest.
 *
 * **Dev builds against the mock only.** `import.meta.env.DEV` is a literal
 * `false` in a real build, so the whole component is eliminated, like the rest
 * of this stack; and against the deployed API it renders nothing, because
 * there is no `/dev/hold` there and a control that silently fails is worse
 * than none.
 *
 * Fourth in the bottom-left dev stack, above the tour switch.
 */
export default function SeatAs() {
  if (!import.meta.env.DEV || API_TARGET !== 'mock') return null;
  return <Picker />;
}

interface Row { guestId: string; fullName: string; table?: string }

/** Table order, then the couple's, then nobody's: the order a planner reads. */
const rank = (table?: string) => {
  const t = tableFor(table);
  return t === 'couple' ? 0 : t ? t.no : TABLES.length + 1;
};

function Picker() {
  const session = useSession();
  const [rows, setRows] = useState<Row[] | null>(null);
  const [mine, setMine] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const userId = session?.userId;
  useEffect(() => {
    if (!userId) return;
    api<{ rows: Row[]; mine: string | null }>('/dev/rows')
      .then((r) => { setRows(r.rows); setMine(r.mine); })
      .catch(() => setRows([]));
  }, [userId]);

  if (!session || !rows?.length) return null;

  const groups = new Map<string, Row[]>();
  for (const r of [...rows].sort((a, b) => rank(a.table) - rank(b.table) || a.fullName.localeCompare(b.fullName))) {
    const key = r.table ?? 'No table';
    groups.set(key, [...(groups.get(key) ?? []), r]);
  }

  const become = async (guestId: string) => {
    setBusy(true);
    try {
      await api('/dev/hold', { method: 'POST', json: { guestId } });
      setMine(guestId);
      await loadProfile();
    } finally {
      setBusy(false);
    }
  };

  const table = session.trip?.table;
  return (
    <details className="seat-as">
      <summary>Seat: {table ?? 'none'}</summary>
      <div className="clock-bar-panel">
        <p className="clock-bar-note">Become another guest (mock only).</p>
        <select value={mine ?? ''} disabled={busy} onChange={(e) => become(e.target.value)} aria-label="Guest">
          {!mine && <option value="">—</option>}
          {[...groups].map(([label, list]) => (
            <optgroup key={label} label={label}>
              {list.map((r) => <option key={r.guestId} value={r.guestId}>{r.fullName}</option>)}
            </optgroup>
          ))}
        </select>
      </div>
    </details>
  );
}
